import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import apiService from '../services/api';

const CONSENT_STORAGE_KEY = 'cookieConsent';
const CONSENT_EXPIRY_DAYS = 365; // GDPR requirement: 1 year

const CookieConsentContext = createContext(null);

function isConsentExpired(timestamp) {
  if (!timestamp) return true;
  const expiryDate = new Date(timestamp);
  expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);
  return new Date() > expiryDate;
}

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadConsent = useCallback(() => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        setConsent(null);
        setLoading(false);
        return;
      }
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (isConsentExpired(parsed.timestamp)) {
          localStorage.removeItem(CONSENT_STORAGE_KEY);
          setConsent(null);
        } else {
          setConsent(parsed);
        }
      } else {
        setConsent(null);
      }
    } catch (error) {
      console.error('Error loading cookie consent:', error);
      setConsent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConsent();
  }, [loadConsent]);

  const hasConsent = useCallback(() => {
    if (consent === null) return false;
    return !isConsentExpired(consent.timestamp);
  }, [consent]);

  const getConsentValue = useCallback(() => {
    if (!hasConsent()) return null;
    return {
      essential: true,
      analytics: consent.analytics || false,
      marketing: consent.marketing || false,
    };
  }, [consent, hasConsent]);

  /* eslint-disable react-hooks/exhaustive-deps */
  const consentValue = useMemo(() => getConsentValue(), [
    consent === null,
    consent?.analytics,
    consent?.marketing,
    consent?.timestamp,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const saveConsent = useCallback(async (preferences) => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        console.warn('localStorage not available');
        return false;
      }
      const consentData = {
        essential: true,
        analytics: preferences.analytics || false,
        marketing: preferences.marketing || false,
        timestamp: Date.now(),
        version: '1.0',
      };
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentData));
      setConsent(consentData);
      try {
        const token = apiService.getToken();
        if (token) {
          await apiService.post('/api/accounts/cookie-consent/', {
            analytics: preferences.analytics || false,
            marketing: preferences.marketing || false,
          });
        }
      } catch (_) {
        console.log('Could not sync consent with backend (user not authenticated)');
      }
      return true;
    } catch (error) {
      console.error('Error saving cookie consent:', error);
      return false;
    }
  }, []);

  const clearConsent = useCallback(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
    }
    setConsent(null);
  }, []);

  const value = useMemo(
    () => ({
      consent: consentValue,
      hasConsent,
      loading,
      saveConsent,
      clearConsent,
      reload: loadConsent,
    }),
    [consentValue, hasConsent, loading, saveConsent, clearConsent, loadConsent]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsentContext() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsentContext must be used within CookieConsentProvider');
  }
  return ctx;
}

export { CookieConsentContext };
